// API клиент для интеграции с backend
// По умолчанию используем относительные URL, чтобы Mini App корректно работал на app.nomafood.ru
// и не был привязан к конкретному домену.
// В dev оставляем прежнее поведение (удобно запускать фронт отдельно без прокси).
const MODE = (import.meta as any).env?.MODE as string | undefined;
const DEFAULT_API_BASE_URL = MODE === 'development' ? 'https://nomafood.ru/api' : '/api';
const DEFAULT_MINIAPP_API_BASE_URL = MODE === 'development' ? 'https://nomafood.ru/api/miniapp' : '/api/miniapp';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || DEFAULT_API_BASE_URL;
const MINIAPP_API_BASE_URL = (import.meta as any).env?.VITE_MINIAPP_API_BASE_URL || DEFAULT_MINIAPP_API_BASE_URL;

export interface Meal {
  id: string;
  name: string;
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
  weight: number;
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  date: string;
  time: string;
  userId: string;
  imageUrl?: string;
  description?: string;
  confidence?: number;
  validationLevel?: string;
  crossChecked?: boolean;
}

export interface UserProfile {
  id: string;
  telegramId: number;
  username: string;
  firstName: string;
  lastName?: string;
  weight: number;
  height: number;
  age: number;
  gender: 'male' | 'female';
  activityLevel: 'low' | 'medium' | 'high';
  goalWeight: number;
  goalCalories: number;
  createdAt: string;
  updatedAt: string;
}

export interface DailyStats {
  date: string;
  totalCalories: number;
  totalProtein: number;
  totalCarbs: number;
  totalFats: number;
  meals: Meal[];
}

export interface WeeklyStats {
  weekStart: string;
  weekEnd: string;
  averageCalories: number;
  averageProtein: number;
  averageCarbs: number;
  averageFats: number;
  totalDays: number;
}

export interface WeightEntry {
  id: string;
  userId: string;
  weight: number;
  date: string;
  bodyFat?: number;
  muscleMass?: number;
  notes?: string;
}

// ===== MINI APP API INTERFACES =====

export interface MiniAppProfile {
  user: {
    id: string;
    telegramId: number;
    username?: string;
    firstName?: string;
    lastName?: string;
    isPremium?: boolean;
    trialEndsAt?: string | null;
    subscriptionEndsAt?: string | null;
  };
  profile: {
    gender: 'male' | 'female';
    age: number;
    weight: number;
    height: number;
    activityLevel: string;
    goal: string;
    targetWeight?: number;
    pace?: string;
    tdee: number;
    protein: number;
    carbs: number;
    fats: number;
    customKBJU?: {
      enabled: boolean;
      calories: number;
      protein: number;
      carbs: number;
      fats: number;
    } | null;

    notificationSettings?: {
      timezone: string;
      mealReminders: Array<{
        id: string;
        hour: number;
        minute: number;
        mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
        enabled: boolean;
      }>;
      macroUnderfill: {
        enabled: boolean;
        hour: number;
        minute: number;
        threshold: number;
      };
      weightTracking: {
        enabled: boolean;
        hour: number;
        minute: number;
        intervalDays: number;
      };
    };
  };
  today: {
    consumed: number;
    remaining: number;
    target: number;
    protein: number;
    carbs: number;
    fats: number;
  };
  targets?: {
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
  };
  stats?: {
    totalDays: number;
    streak: number;
    streakActiveToday?: boolean;
  };
  weightHistory?: Array<{
    date: string;
    weight: number;
    bodyFat?: number;
    muscleMass?: number;
  }>;
}

export interface MiniAppMeal {
  id: string;
  category: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  name: string;
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
  weight: number;
  photoUrl?: string;
  aiAnalysis?: any;
  createdAt: string;
}

export interface MiniAppStats {
  period: 'day' | 'week' | 'month';
  startDate: string;
  endDate: string;
  targets: {
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
  };
  totals: {
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
  };
  averages: {
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
  };
  dailyStats: Array<{
    date: string;
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
    mealsCount: number;
  }>;
  mealsCount: number;
  stats?: {
    totalDays: number;
    streak: number;
    streakActiveToday?: boolean;
  };
}

export interface MiniAppAdviceStatus {
  hasAdvice: boolean;
  generatedAt: string | null;
  nextRefreshAt: string | null;
  localDate: string;
  key: string | null;
}

export interface MiniAppAdvice {
  hasAdvice: boolean;
  title: string;
  tips: string[];
  generatedAt: string;
  nextRefreshAt: string;
  localDate: string;
  key: string;
}

// ===== ACTIVITY INTERFACES =====
export type ActivityType = 'running' | 'walking' | 'cycling' | 'swimming' | 'strength' | 'yoga' | 'sports' | 'other';
export type ActivityIntensity = 'low' | 'medium' | 'high';
export type CaloriesMode = 'add_to_norm' | 'show_separate';

export interface MiniAppActivity {
  id: string;
  type: ActivityType;
  name?: string;
  durationMinutes: number;
  intensity: ActivityIntensity;
  caloriesBurned: number;
  recordedAt: string;
  createdAt: string;
}

export interface ActivitiesResponse {
  activities: MiniAppActivity[];
  summary: {
    totalCalories: number;
    totalDuration: number;
    count: number;
  };
  caloriesMode: CaloriesMode;
}

export interface ActivityAiEstimate {
  type: ActivityType;
  name: string;
  durationMinutes: number;
  intensity: ActivityIntensity;
  met: number;
  caloriesBurned: number;
  confidence: number;
}

class ApiClient {
  private baseUrl: string;
  private miniappBaseUrl: string;
  private authToken: string | null = null;
  private refreshPromise: Promise<string> | null = null; // Prevent multiple simultaneous refreshes

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    this.miniappBaseUrl = MINIAPP_API_BASE_URL;
    this.loadAuthToken();
  }

  private loadAuthToken() {
    this.authToken = localStorage.getItem('miniapp_token');
  }

  private setAuthToken(token: string) {
    this.authToken = token;
    localStorage.setItem('miniapp_token', token);
  }

  private setMiniAppOnboardingRequired(required: boolean) {
    localStorage.setItem('miniapp_onboarding_required', required ? 'true' : 'false');
    try {
      window.dispatchEvent(new Event('miniapp_onboarding_required_changed'));
    } catch {
      // noop
    }
  }

  getMiniAppOnboardingRequired(): boolean {
    return localStorage.getItem('miniapp_onboarding_required') === 'true';
  }

  private clearAuthToken() {
    this.authToken = null;
    localStorage.removeItem('miniapp_token');
  }

  private async refreshToken(): Promise<string | null> {
    // If already refreshing, wait for that promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const tg = (window as any).Telegram?.WebApp;
    const initData = tg?.initData;

    if (!initData) {
      console.warn('⚠️ No initData available for token refresh');
      return null;
    }

    // Create refresh promise to prevent parallel refreshes
    this.refreshPromise = this.startMiniAppSession(initData)
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  private async miniappRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.miniappBaseUrl}${endpoint}`;

    // Use current token
    const token = this.authToken || localStorage.getItem('miniapp_token');

    const baseHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token && !(options.headers as any)?.Authorization) {
      baseHeaders['Authorization'] = `Bearer ${token}`;
    }

    // Add Telegram ID header for backend validation
    try {
      const tg = (window as any).Telegram?.WebApp;
      const tgId = tg?.initDataUnsafe?.user?.id;
      if (tgId && !baseHeaders['x-telegram-id']) {
        baseHeaders['x-telegram-id'] = String(tgId);
      }
    } catch (_) { /* noop */ }

    const config: RequestInit = {
      ...options,
      headers: {
        ...baseHeaders,
        ...options.headers,
      },
    };

    let response = await fetch(url, config);

    // If unauthorized, check if token expired and refresh
    if (response.status === 401) {
      let shouldRetry = false;
      try {
        const errorBody = await response.clone().json();
        // Retry only if token expired (not if invalid/mismatch)
        if (errorBody?.code === 'TOKEN_EXPIRED' || errorBody?.error === 'Token expired') {
          console.log('🔄 Token expired, refreshing...');
          shouldRetry = true;
        } else if (errorBody?.code === 'TOKEN_MISMATCH' || errorBody?.code === 'TOKEN_INVALID') {
          // Typical case: user switched Telegram accounts; re-auth with current initData
          this.clearAuthToken();
          shouldRetry = true;
        } else {
          console.warn('⚠️ Auth failed (not expired):', errorBody);
        }
      } catch {
        // If can't parse JSON, try refresh anyway
        shouldRetry = true;
      }

      if (shouldRetry) {
        const newToken = await this.refreshToken();
        if (newToken) {
          const retryHeaders = {
            ...(config.headers as any),
            Authorization: `Bearer ${newToken}`,
          };
          response = await fetch(url, { ...config, headers: retryHeaders });
        }
      }
    }

    if (!response.ok) {
      let errorBody: any = null;
      try {
        errorBody = await response.clone().json();
      } catch {
        // ignore
      }

      if (response.status === 403 && errorBody?.code === 'ONBOARDING_REQUIRED') {
        this.setMiniAppOnboardingRequired(true);
      }

      if (response.status === 404 && (errorBody?.code === 'PROFILE_NOT_FOUND' || /user not found/i.test(String(errorBody?.error || '')))) {
        this.setMiniAppOnboardingRequired(true);
      }

      const errorText = errorBody ? JSON.stringify(errorBody) : await response.text();
      console.error('❌ miniappRequest failed:', { status: response.status, errorText });
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return (await response.json()) as T;
  }

  // ===== USER PROFILE API =====

  /**
   * Получение профиля пользователя по Telegram ID
   */
  async getUserProfile(telegramId: number): Promise<UserProfile> {
    const response = await this.request<{ success: boolean; data: UserProfile }>(
      `/users/telegram/${telegramId}`
    );
    return response.data;
  }

  /**
   * Обновление профиля пользователя
   */
  async updateUserProfile(userId: string, profileData: Partial<UserProfile>): Promise<UserProfile> {
    const response = await this.request<{ success: boolean; data: UserProfile }>(
      `/users/${userId}`,
      {
        method: 'PUT',
        body: JSON.stringify(profileData),
      }
    );
    return response.data;
  }

  // ===== MEALS API =====

  /**
   * Получение приемов пищи пользователя за день
   */
  async getDailyMeals(userId: string, date: string): Promise<Meal[]> {
    const response = await this.request<{ success: boolean; data: Meal[] }>(
      `/meals/user/${userId}/daily?date=${date}`
    );
    return response.data;
  }

  /**
   * Получение приемов пищи пользователя за неделю
   */
  async getWeeklyMeals(userId: string, startDate: string): Promise<Meal[]> {
    const response = await this.request<{ success: boolean; data: Meal[] }>(
      `/meals/user/${userId}/weekly?startDate=${startDate}`
    );
    return response.data;
  }

  /**
   * Создание нового приема пищи
   */
  async createMeal(userId: string, mealData: Omit<Meal, 'id' | 'userId'>): Promise<Meal> {
    const response = await this.request<{ success: boolean; data: Meal }>(
      '/meals',
      {
        method: 'POST',
        body: JSON.stringify({ ...mealData, userId }),
      }
    );
    return response.data;
  }

  /**
   * Обновление приема пищи
   */
  async updateMeal(mealId: string, mealData: Partial<Meal>): Promise<Meal> {
    const response = await this.request<{ success: boolean; data: Meal }>(
      `/meals/${mealId}`,
      {
        method: 'PUT',
        body: JSON.stringify(mealData),
      }
    );
    return response.data;
  }

  /**
   * Удаление приема пищи
   */
  async deleteMeal(mealId: string): Promise<void> {
    await this.request(`/meals/${mealId}`, {
      method: 'DELETE',
    });
  }

  // ===== STATISTICS API =====

  /**
   * Получение статистики за день
   */
  async getDailyStats(userId: string, date: string): Promise<DailyStats> {
    const response = await this.request<{ success: boolean; data: DailyStats }>(
      `/stats/daily/${userId}?date=${date}`
    );
    return response.data;
  }

  /**
   * Получение статистики за неделю
   */
  async getWeeklyStats(userId: string, startDate: string): Promise<WeeklyStats> {
    const response = await this.request<{ success: boolean; data: WeeklyStats }>(
      `/stats/weekly/${userId}?startDate=${startDate}`
    );
    return response.data;
  }

  // ===== WEIGHT TRACKING API =====

  /**
   * Получение записей веса пользователя
   */
  async getWeightHistory(userId: string, limit: number = 30): Promise<WeightEntry[]> {
    const response = await this.request<{ success: boolean; data: WeightEntry[] }>(
      `/users/${userId}/weight?limit=${limit}`
    );
    return response.data;
  }

  /**
   * Добавление новой записи веса
   */
  async addWeightEntry(userId: string, weight: number, date?: string): Promise<WeightEntry> {
    const response = await this.request<{ success: boolean; data: WeightEntry }>(
      `/users/${userId}/weight`,
      {
        method: 'POST',
        body: JSON.stringify({ weight, date: date || new Date().toISOString() }),
      }
    );
    return response.data;
  }

  /**
   * Добавить запись веса по telegramId текущего пользователя (удобно из MiniApp)
   */
  async addMyWeight(weight: number, date?: string): Promise<{ data: WeightEntry; goalReached?: boolean; goalMessage?: string }> {
    if (!this.authToken) throw new Error('No auth token available');

    // Убедимся, что вес - число
    const numWeight = typeof weight === 'string' ? parseFloat(weight) : weight;
    if (!Number.isFinite(numWeight) || numWeight <= 0 || numWeight > 500) {
      throw new Error('Invalid weight value');
    }

    console.log('📤 Sending weight to API:', { weight: numWeight, date });

    const res = await this.miniappRequest<{ success: boolean; data: WeightEntry; goalReached?: boolean; goalMessage?: string }>(
      '/weight',
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${this.authToken}` },
        body: JSON.stringify({ weight: numWeight, date })
      }
    );

    console.log('✅ Weight API response:', res);
    return { data: res.data, goalReached: res.goalReached, goalMessage: res.goalMessage };
  }

  // ===== SYNC API =====

  /**
   * Получение полных данных пользователя для синхронизации
   */
  async getFullUserData(telegramId: number): Promise<{
    user: UserProfile;
    todayMeals: Meal[];
    weeklyStats: WeeklyStats;
    weightHistory: WeightEntry[];
  }> {
    const response = await this.request<{
      success: boolean;
      data: {
        user: UserProfile;
        todayMeals: Meal[];
        weeklyStats: WeeklyStats;
        weightHistory: WeightEntry[];
      };
    }>(`/sync/user/${telegramId}`);
    return response.data;
  }

  /**
   * Синхронизация данных между приложением и ботом
   */
  async syncData(telegramId: number, data: {
    meals?: Meal[];
    weightEntries?: WeightEntry[];
    profileUpdates?: Partial<UserProfile>;
  }): Promise<{
    success: boolean;
    message: string;
    syncedItems: number;
  }> {
    const response = await this.request<{
      success: boolean;
      data: {
        message: string;
        syncedItems: number;
      };
    }>(`/sync/user/${telegramId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return {
      success: response.success,
      message: response.data.message,
      syncedItems: response.data.syncedItems,
    };
  }

  // ===== TELEGRAM INTEGRATION =====

  /**
   * Получение Telegram WebApp данных
   */
  getTelegramData(): { initData: string; user?: any } | null {
    if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp as any;
      return {
        initData: tg.initData || '',
        user: tg.initDataUnsafe?.user,
      };
    }
    return null;
  }

  /**
   * Получение Telegram ID пользователя
   */
  getTelegramUserId(): number | null {
    const tgData = this.getTelegramData();
    return tgData?.user?.id || null;
  }

  // ===== MINI APP API =====

  /**
   * Получение dev токена для локального тестирования
   */
  async getDevToken(telegramId: number, firstName: string = 'Test', username: string = 'testuser'): Promise<string> {
    const response = await this.miniappRequest<{ success: boolean; token: string; onboardingRequired?: boolean }>(
      '/session/dev',
      {
        method: 'POST',
        body: JSON.stringify({ telegramId, firstName, username }),
      }
    );
    this.setMiniAppOnboardingRequired(!!response.onboardingRequired);
    this.setAuthToken(response.token);
    return response.token;
  }

  /**
   * Старт сессии Mini App через Telegram initData
   */
  async startMiniAppSession(initData: string): Promise<string> {
    const response = await this.miniappRequest<{ success: boolean; token: string; onboardingRequired?: boolean }>(
      '/session/start',
      {
        method: 'POST',
        body: JSON.stringify({ initData })
      }
    );
    // Важно: обновляем внутренний токен, а не только localStorage
    this.setMiniAppOnboardingRequired(!!response.onboardingRequired);
    this.setAuthToken(response.token);
    return response.token;
  }

  /**
   * Получение профиля пользователя через Mini App API
   */
  async getMiniAppProfile(): Promise<MiniAppProfile> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: MiniAppProfile }>(
      '/me',
      {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
      }
    );
    return response.data;
  }

  /**
   * Получение приёмов пищи через Mini App API
   */
  async getMiniAppMeals(date?: string, limit: number = 20, offset: number = 0): Promise<{
    meals: MiniAppMeal[];
    pagination: {
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (date) {
      params.append('date', date);
    }

    const response = await this.miniappRequest<{ success: boolean; data: any }>(
      `/meals?${params}`,
      {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
      }
    );
    return response.data;
  }

  /**
   * Создание приёма пищи через Mini App API
   */
  async createMiniAppMeal(mealData: {
    category: 'breakfast' | 'lunch' | 'dinner' | 'snack';
    name: string;
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
    weight?: number;
  }): Promise<MiniAppMeal> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: { meal: MiniAppMeal } }>(
      '/meals',
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
        body: JSON.stringify(mealData),
      }
    );
    return response.data.meal;
  }

  /**
   * Удаление приёма пищи через Mini App API
   */
  async deleteMiniAppMeal(mealId: string): Promise<void> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    await this.miniappRequest<{ success: boolean; message: string }>(
      `/meals/${mealId}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
      }
    );
  }

  /**
   * Получение статистики через Mini App API
   */
  async getMiniAppStats(period: 'day' | 'week' | 'month' = 'week', date?: string): Promise<MiniAppStats> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const qs = new URLSearchParams({ period });
    if (date) qs.set('date', date);

    const response = await this.miniappRequest<{ success: boolean; data: MiniAppStats }>(
      `/stats?${qs.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
      }
    );
    return response.data;
  }

  /**
   * Статус советов (есть ли подсказки прямо сейчас)
   */
  async getMiniAppAdviceStatus(): Promise<MiniAppAdviceStatus> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: MiniAppAdviceStatus }>(
      '/advice/status',
      {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
      }
    );
    return response.data;
  }

  /**
   * Получить советы (3 подсказки на сегодня)
   */
  async getMiniAppAdvice(): Promise<MiniAppAdvice> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: MiniAppAdvice }>(
      '/advice',
      {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
      }
    );
    return response.data;
  }

  /**
   * Проверка наличия токена авторизации
   */
  hasAuthToken(): boolean {
    return !!this.authToken;
  }

  /**
   * Получение текущего токена
   */
  getAuthToken(): string | null {
    return this.authToken;
  }

  // ===== ACTIVITIES API =====

  /**
   * Получение активностей за день
   */
  async getActivities(date?: string): Promise<ActivitiesResponse> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const params = date ? `?date=${date}` : '';
    // Backend returns { activities, stats, caloriesMode } (legacy frontend expects { activities, summary, caloriesMode })
    const response = await this.miniappRequest<{ success: boolean; data: any }>(
      `/activities${params}`,
      {
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );

    const raw = response.data || {};
    const stats = raw.stats || {};
    const summary = raw.summary || {
      totalCalories: Number(stats.totalCaloriesBurned ?? stats.totalCalories ?? 0),
      totalDuration: Number(stats.totalDurationMinutes ?? stats.totalDuration ?? 0),
      count: Number(stats.activitiesCount ?? stats.count ?? 0),
    };

    return {
      activities: raw.activities || [],
      summary,
      caloriesMode: (raw.caloriesMode as CaloriesMode) || 'show_separate',
    };
  }

  /**
   * Создание активности
   */
  async createActivity(activityData: {
    type: ActivityType;
    name?: string;
    durationMinutes: number;
    intensity?: ActivityIntensity;
    caloriesBurned?: number;
    recordedAt?: string;
  }): Promise<MiniAppActivity> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: { activity: MiniAppActivity } }>(
      '/activities',
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
        body: JSON.stringify(activityData),
      }
    );
    return response.data.activity;
  }

  /**
   * Удаление активности
   */
  async deleteActivity(activityId: string): Promise<void> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    await this.miniappRequest<{ success: boolean }>(
      `/activities/${activityId}`,
      {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
  }

  /**
   * Получение режима калорий для активностей
   */
  async getCaloriesMode(): Promise<CaloriesMode> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: { mode: CaloriesMode } }>(
      '/activities/calories-mode',
      {
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
    return response.data.mode;
  }

  /**
   * Установка режима калорий для активностей
   */
  async setCaloriesMode(mode: CaloriesMode): Promise<void> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    await this.miniappRequest<{ success: boolean }>(
      '/activities/calories-mode',
      {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
        body: JSON.stringify({ mode }),
      }
    );
  }

  /**
   * Расчёт калорий для активности (предпросмотр)
   */
  async calculateActivityCalories(type: ActivityType, durationMinutes: number, intensity: ActivityIntensity = 'medium'): Promise<number> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: { calories: number } }>(
      `/activities/calculate?type=${type}&durationMinutes=${durationMinutes}&intensity=${intensity}`,
      {
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
    return response.data.calories;
  }

  /**
   * AI-парсинг свободного текста активности (возвращает оценку + калории)
   */
  async estimateActivityFromText(text: string): Promise<ActivityAiEstimate> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: ActivityAiEstimate }>(
      '/activities/ai-estimate',
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
        body: JSON.stringify({ text }),
      }
    );
    return response.data;
  }
}

// Создаем экземпляр API клиента
export const apiClient = new ApiClient();

const apiHooks = {
  // Generic request method for custom endpoints
  request: async (endpoint: string, options: RequestInit = {}): Promise<any> => {
    const token = localStorage.getItem('miniapp_token');
    const baseHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      baseHeaders['Authorization'] = `Bearer ${token}`;
    }
    try {
      const tg = (window as any).Telegram?.WebApp;
      const tgId = tg?.initDataUnsafe?.user?.id;
      if (tgId) {
        baseHeaders['x-telegram-id'] = String(tgId);
      }
    } catch (_) { /* noop */ }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: { ...baseHeaders, ...options.headers },
    });
    return response.json();
  },

  // User Profile
  getUserProfile: apiClient.getUserProfile.bind(apiClient),
  updateUserProfile: apiClient.updateUserProfile.bind(apiClient),

  // Meals
  getDailyMeals: apiClient.getDailyMeals.bind(apiClient),
  getWeeklyMeals: apiClient.getWeeklyMeals.bind(apiClient),
  createMeal: apiClient.createMeal.bind(apiClient),
  updateMeal: apiClient.updateMeal.bind(apiClient),
  deleteMeal: apiClient.deleteMeal.bind(apiClient),

  // Statistics
  getDailyStats: apiClient.getDailyStats.bind(apiClient),
  getWeeklyStats: apiClient.getWeeklyStats.bind(apiClient),

  // Weight Tracking
  getWeightHistory: apiClient.getWeightHistory.bind(apiClient),
  addWeightEntry: apiClient.addWeightEntry.bind(apiClient),
  addMyWeight: apiClient.addMyWeight.bind(apiClient),

  // Sync
  getFullUserData: apiClient.getFullUserData.bind(apiClient),
  syncData: apiClient.syncData.bind(apiClient),

  // Telegram
  getTelegramData: apiClient.getTelegramData.bind(apiClient),
  getTelegramUserId: apiClient.getTelegramUserId.bind(apiClient),

  // Mini App API
  getDevToken: apiClient.getDevToken.bind(apiClient),
  startMiniAppSession: apiClient.startMiniAppSession.bind(apiClient),
  getMiniAppProfile: apiClient.getMiniAppProfile.bind(apiClient),
  getMiniAppMeals: apiClient.getMiniAppMeals.bind(apiClient),
  createMiniAppMeal: apiClient.createMiniAppMeal.bind(apiClient),
  deleteMiniAppMeal: apiClient.deleteMiniAppMeal.bind(apiClient),
  getMiniAppStats: apiClient.getMiniAppStats.bind(apiClient),
  getMiniAppAdviceStatus: apiClient.getMiniAppAdviceStatus.bind(apiClient),
  getMiniAppAdvice: apiClient.getMiniAppAdvice.bind(apiClient),
  getMiniAppOnboardingRequired: apiClient.getMiniAppOnboardingRequired.bind(apiClient),
  hasAuthToken: apiClient.hasAuthToken.bind(apiClient),
  getAuthToken: apiClient.getAuthToken.bind(apiClient),

  // Activities
  getActivities: apiClient.getActivities.bind(apiClient),
  createActivity: apiClient.createActivity.bind(apiClient),
  deleteActivity: apiClient.deleteActivity.bind(apiClient),
  getCaloriesMode: apiClient.getCaloriesMode.bind(apiClient),
  setCaloriesMode: apiClient.setCaloriesMode.bind(apiClient),
  calculateActivityCalories: apiClient.calculateActivityCalories.bind(apiClient),
  estimateActivityFromText: apiClient.estimateActivityFromText.bind(apiClient),
};

// Хук для работы с API в React компонентах
export function useApi() {
  return apiHooks;
}

export default apiClient; 